import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import {
   Main, Styled,
   ReceiverSection, ReceiverIndexBadge, SectionHeader,
   ScrollableFieldsContainer, FieldContainer, FieldHeader, ButtonGroup, ActionButton, FieldGroup,
   MappingRow, MappingInput, RemoveBtn, AddMappingBtn
} from "./style";
import { Select } from "../../../../form/select";
import { updateNodeValue } from "../../../../../services/method";
import axios from "axios";
import { webURL } from "../../../../../services/API";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { TextInput } from "../../../../form/textInput";
import { useSearchParams } from "react-router-dom";

type ReceiverField = { id: string; name: string };
type MappingEntry = { id: string; key: string; value: string };

type KnowledgeProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function SubWorkflow({ id, data, setChildMinimize, setChildSettings }: KnowledgeProps) {
   const [searchParams] = useSearchParams();
   const { setNodes } = useReactFlow();
   const workflowId = React.useMemo(() => data.value?.workflows?.find((workflow: any) => workflow.isActive)?.id, [data.value?.workflows]);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   const mode: 'trigger' | 'receiver' = data.value?.mode || 'trigger';

   const [modes] = React.useState([
      { id: 1, name: 'Sender', refId: 'trigger', isActive: mode === 'trigger' },
      { id: 2, name: 'Receiver', refId: 'receiver', isActive: mode === 'receiver' },
   ]);

   const [receiverFields, setReceiverFields] = React.useState<ReceiverField[]>(
      data.value?.receiverFields?.length ? data.value.receiverFields : [{ id: '1', name: '' }]
   );

   const [mappings, setMappings] = React.useState<MappingEntry[]>(
      data.value?.mappings?.length ? data.value.mappings : [{ id: '1', key: '', value: '' }]
   );

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      (async function() {
         const response = (await axios.get(`${webURL}/projects?select=name`)).data;
         updateNodeValue(id, setNodes, { 
            workflows: response.filter((i: any) => i._id !== searchParams.get('id')).map((item: any, index: number) => ({ 
               id: item._id, name: item.name, 
               isActive: workflowId ? workflowId === item._id : index === 0 
            })),
            mode: data.value?.mode || 'trigger',
         });
      })();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // sync receiverFields to node value
   React.useEffect(() => {
      updateNodeValue(id, setNodes, { receiverFields });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [receiverFields]);

   // sync mappings to node value
   React.useEffect(() => {
      updateNodeValue(id, setNodes, { mappings });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [mappings]);

   // ---
   const handleSelectMode = (selectId: number) => {
      const selectedMode = selectId === 1 ? 'trigger' : 'receiver';
      updateNodeValue(id, setNodes, { mode: selectedMode });
   };

   // ---
   const handleSelectWorkflow = (selectId: number) => {
      updateNodeValue(id, setNodes, { workflows: data.value?.workflows.map((item: any) => ({ ...item, isActive: item.id === selectId })) });
   };

   // ---
   const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
      const value = e.target.value;
      if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: value });
   };

   // --- Receiver field handlers ---
   const handleAddReceiverField = () => {
      setReceiverFields(prev => [...prev, { id: Date.now().toString(), name: '' }]);
   };
   const handleRemoveReceiverField = (fieldId: string) => {
      setReceiverFields(prev => prev.filter(f => f.id !== fieldId));
   };
   const handleReceiverFieldName = (fieldId: string, name: string) => {
      setReceiverFields(prev => prev.map(f => f.id === fieldId ? { ...f, name } : f));
   };

   // --- Mapping handlers ---
   const handleAddMapping = () => {
      setMappings(prev => [...prev, { id: Date.now().toString(), key: '', value: '' }]);
   };
   const handleRemoveMapping = (entryId: string) => {
      setMappings(prev => prev.filter(m => m.id !== entryId));
   };
   const handleMappingChange = (entryId: string, field: 'key' | 'value', val: string) => {
      setMappings(prev => prev.map(m => m.id === entryId ? { ...m, [field]: val } : m));
   };

   // ---
   const isWarning = false;

   const modeOptions = modes.map(m => ({ ...m, isActive: m.refId === mode }));

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : null }
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ width: '420px' }}
         ><Styled>
            <Select title="Mode" data={ modeOptions } handleSelectItem={ handleSelectMode } isSmall={ true } />

            { mode === 'receiver' ? (
               <ReceiverSection>
                  <ReceiverIndexBadge>
                     Node ID: <span>{ data.index }</span>
                     <br />
                     <small style={{ opacity: 0.7 }}>Use this ID in the Trigger workflow to send variables here</small>
                  </ReceiverIndexBadge>
                  <SectionHeader><h3>Expected Input Fields</h3></SectionHeader>
                  <ScrollableFieldsContainer>
                     { receiverFields.map((field, index) => (
                        <FieldContainer key={ field.id }>
                           <FieldHeader>
                              <span>Field { index + 1 }</span>
                              <ButtonGroup>
                                 <ActionButton className="add-btn" onClick={ handleAddReceiverField }>+</ActionButton>
                                 { receiverFields.length > 1 && (
                                    <ActionButton className="remove-btn" onClick={ () => handleRemoveReceiverField(field.id) }>🗑</ActionButton>
                                 )}
                              </ButtonGroup>
                           </FieldHeader>
                           <FieldGroup>
                              <label>Field Name</label>
                              <TextInput
                                 value={ field.name }
                                 handleChange={ e => handleReceiverFieldName(field.id, e.target.value) }
                                 placeholder="e.g. customerName"
                              />
                           </FieldGroup>
                        </FieldContainer>
                     ))}
                  </ScrollableFieldsContainer>
               </ReceiverSection>
            ) : (
               <>
                  <div className="line"><span>Select workflow</span></div>
                  <Select title="Workflow" data={ data.value?.workflows || [] } handleSelectItem={ handleSelectWorkflow } isSmall={ true } />
                  <div className="line"><span>Or import workflow</span></div>
                  <TextInput keyRef="workflow_id" placeholder="Workflow ID" value={ data.value?.workflow_id || '' } handleChange={ e => handleChangeInput(e, 'workflow_id') } />
                  <TextInput keyRef="api_key" placeholder="Authorization (API Key)" value={ data.value?.api_key || '' } handleChange={ e => handleChangeInput(e, 'api_key') } />
                  <div className="line"><span>Variable mappings</span></div>
                  <FieldGroup>
                     <label>Receiver Node ID</label>
                     <TextInput
                        keyRef="receiver_node_index"
                        placeholder="e.g. subWorkflow-1"
                        value={ data.value?.receiver_node_index || '' }
                        handleChange={ e => handleChangeInput(e, 'receiver_node_index') }
                     />
                  </FieldGroup>
                  <SectionHeader>
                     <h3>Key</h3>
                     <h3 style={{ marginLeft: 'auto', marginRight: mappings.length > 1 ? '32px' : '8px' }}>Value (drag variables here)</h3>
                  </SectionHeader>
                  { mappings.map(entry => (
                     <MappingRow key={ entry.id }>
                        <MappingInput
                           placeholder="key"
                           value={ entry.key }
                           onChange={ e => handleMappingChange(entry.id, 'key', e.target.value) }
                        />
                        <TextInput
                           placeholder="{{nodeIndex}}"
                           value={ entry.value }
                           onVariableDrop={ val => handleMappingChange(entry.id, 'value', val) }
                           handleChange={ e => handleMappingChange(entry.id, 'value', e.target.value) }
                        />
                        { mappings.length > 1 && (
                           <RemoveBtn onClick={ () => handleRemoveMapping(entry.id) }>✕</RemoveBtn>
                        )}
                     </MappingRow>
                  ))}
                  <AddMappingBtn onClick={ handleAddMapping }>+ Add mapping</AddMappingBtn>
               </>
            )}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}